import {useState, useRef, useEffect, memo} from 'react';

import styled from 'styled-components';
import {noop} from 'lodash';

import {flexCenterVertically} from '~/shared/theme/FlexLayout';
import usePreventBodyScrolling from '~/shared/hooks/usePreventBodyScrolling';
import {getLocalizationService} from '~/shared/services/localisationService';
import HamburgerImg from '~/assets/images/homepage/hamburger.svg?react';
import Button from '~/shared/components/Button';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {User} from '~/shared/store/models';
import {ActionMenuLink} from '~/shared/components/header/ActionMenu/types';
import {PrivateUserActionMenuContent, CompanyUserActionMenuContent} from '~/shared/components/header/ActionMenu';
import {UserStatus} from '~/shared/components/header';
import {useMoneyCards} from '~/shared/hooks';
import {UserDeviceState} from '~/shared/store/storeModules/userDevice/userDeviceReducer';

const ActionMenuButton = styled(Button)`
  ${flexCenterVertically}
  cursor: pointer;
  color: ${({theme}) => theme.colors.primaryText};
`;

const MenuBurgerButton = styled(HamburgerImg)`
  display: block;
  width: 20px;
  height: 20px;
  ${flipOnLTR`
    margin-right: 8px;
  `}
`;

interface ActionMenuProps {
  actionMenuLinks: ActionMenuLink[];
  burgerMenu: boolean;
  CustomMenuComponent?: React.FC<{isOpen?: boolean}>;
  className?: string;
  userData?: User;
  userDevice: UserDeviceState;
}

const ActionMenu = ({actionMenuLinks, burgerMenu, CustomMenuComponent, className, userData, userDevice} : ActionMenuProps) => {
  const {t} = getLocalizationService();
  const [isOpen, setIsOpen] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [menuContentOffset, setMenuContentOffset] = useState<number | undefined>(undefined);
  // fetched on mount so links that depend on the user's cards (e.g. tap & save) are resolved by the time the menu opens
  const {tenbisCreditBalance, hasWalletCard, loaded: isMoneyCardsLoaded, loading: isMoneyCardsLoading, refetch: fetchMoneyCards, error: moneyCardsError} = useMoneyCards();

  useEffect(() => {
    if (actionMenuRef.current) {
      const rect = actionMenuRef.current.getBoundingClientRect();
      setMenuContentOffset(rect.top + rect.height);
    }
  }, []);
  
  const toggleMenuOpen = (clickEvent?: Event) => {
    if (clickEvent) {
      clickEvent.preventDefault();
    }

    setIsOpen(state => !state);
  };

  usePreventBodyScrolling({shouldPreventScroll: !!isOpen});

  const MenuContentComponent = (userData && !userDevice.isMinLargeMobile) ? PrivateUserActionMenuContent : CompanyUserActionMenuContent;

  return (
    <ClickOutsideDiv id="header-action-menu-container" onClickOutside={isOpen ? toggleMenuOpen : noop}>
      <ActionMenuButton
        className={className}
        data-test-id="sideMenuBtn"
        onClick={toggleMenuOpen}
        aria-controls="header-action-menu-container"
        isExpandable
        ref={actionMenuRef}
      >
        {!burgerMenu ? <UserStatus /> : (CustomMenuComponent ? <CustomMenuComponent isOpen={isOpen}/> : <MenuBurgerButton alt={t('menu')}/>)}
      </ActionMenuButton>
      {isOpen && (
        <MenuContentComponent
          actionMenuLinks={actionMenuLinks}
          toggleMenuOpen={toggleMenuOpen}
          burgerMenu={burgerMenu}
          topOffset={menuContentOffset}
          userData={userData}
          userDevice={userDevice}
          tenbisCreditBalance={tenbisCreditBalance}
          isMoneyCardsLoaded={isMoneyCardsLoaded}
          isMoneyCardsLoading={isMoneyCardsLoading}
          fetchMoneyCards={fetchMoneyCards}
          moneyCardsError={moneyCardsError}
          hasWalletCard={hasWalletCard}
        />
      )}
    </ClickOutsideDiv>
  );
};

export default memo(ActionMenu);
